/**
 * @description AcceptInvite component for completing new user invitation or signup verification
 *
 * Key features:
 * - Password form with confirmation and strength indicators
 * - Reads passwordResetCode + email from URL params (set by InviteEmailService branded email)
 * - Backwards compatible with email_auth_code param (Wristband EMAIL-type invites)
 * - Context-aware copy: ?context=signup shows signup-specific headings/errors
 * - Calls POST /api/auth/reset-password with passwordResetCode
 * - Redirects to Wristband auth session on success (auto-login via immediate login)
 * - Dark auth-card styling aligned with SignIn / ForgotPassword (brand tokens)
 *
 * @dependencies
 * - AuthLayout: Shared auth page layout
 * - apiClient: API calls to backend
 * - authTenantQuery: SO slug + minted wristbandTenant hop allowlist
 *
 * @notes
 * - Local preview (no real invite): /accept-invite?passwordResetCode=preview&context=signup
 * - passwordResetCode comes from branded email (Password Reset Link API → InviteEmailService or SignupEmailService)
 * - email_auth_code is the legacy path for Wristband EMAIL-type invites
 * - context=signup: user came from signup verification email (SignupEmailService)
 * - Same password strength requirements as ResetPassword
 * - Uses Wristband's Password Reset flow (per Wristband team recommendation)
 * - On PASSWORD_RESET_COMPLETED_WITH_IMMEDIATE_LOGIN, redirects via auth session code → auto-login
 * - CP admin./app. doors follow the registered api /router/login hop (LYN-335)
 * - Passes minted `wristbandTenant` into shouldFollowAuthRedirect so only that
 *   Wristband vanity is followed. LynxPay / platform hops are always refused.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import AuthLayout from './AuthLayout';
import { useBranding } from '../../contexts/BrandingContext';
import { resolveAuthSubdomain, shouldFollowAuthRedirect, withAuthSubdomain } from '../../utils/authTenantQuery';

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) },
];

const checkPasswordStrength = (pw) => {
  const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(pw));
  return {
    passed,
    isStrong: passed.length === PASSWORD_REQUIREMENTS.length
  };
};

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const { slug: brandingSlug } = useBranding();

  // Password reset link invites (branded emails): ?passwordResetCode=...&email=...
  const passwordResetCode = searchParams.get('passwordResetCode');
  // EMAIL-type invites (legacy Wristband emails): ?email_auth_code=...
  const emailAuthCode = searchParams.get('email_auth_code');

  // Context: 'signup' from SignupEmailService, null from InviteEmailService
  const context = searchParams.get('context');
  const isSignupContext = context === 'signup';
  const mintedWristbandTenant = searchParams.get('wristbandTenant');

  // At least one invite identifier must be present
  const hasValidCode = !!(passwordResetCode || emailAuthCode);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCodeUsed, setIsCodeUsed] = useState(false);

  // Memoize password strength to avoid recalculating on unrelated re-renders
  const passwordStrength = useMemo(() => checkPasswordStrength(password), [password]);

  // Check for valid code on mount
  useEffect(() => {
    if (!hasValidCode) {
      setError(isSignupContext
        ? 'Invalid or missing verification link. Please try signing up again.'
        : 'Invalid or missing invitation link. Please contact your administrator.'
      );
    }
  }, [hasValidCode, isSignupContext]);

  const validateForm = () => {
    const errors = {};

    if (!password) {
      errors.password = 'Password is required';
    } else if (!passwordStrength.isStrong) {
      errors.password = 'Password does not meet requirements';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field === 'password') {
      setPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }

    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasValidCode) {
      setError(isSignupContext
        ? 'Invalid verification link. Please try signing up again.'
        : 'Invalid invitation link. Please contact your administrator.'
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Build request body based on invite type
      const body = { newPassword: password };
      if (passwordResetCode) {
        // Password reset link invite (branded email via InviteEmailService)
        body.passwordResetCode = passwordResetCode;
      } else if (emailAuthCode) {
        // EMAIL-type invite (legacy Wristband email)
        body.emailAuthCode = emailAuthCode;
      }

      // URL subdomain (email) → branding → lynx.build parse → Tier-3 resolve.
      // getLynxBuildTenantSlug alone is null on app.{custom} after Worker 301.
      const subdomain = await resolveAuthSubdomain({ brandingSlug, searchParams });
      const resetUrl = withAuthSubdomain('/auth/reset-password', subdomain);

      const response = await apiClient.post(resetUrl, body);

      if (response.data.success) {
        if (response.data.redirectUrl
          && shouldFollowAuthRedirect(response.data.redirectUrl, undefined, mintedWristbandTenant)) {
          // Redirect through Wristband auth session → auto-login
          window.location.href = response.data.redirectUrl;
        } else {
          // Non-redirect flows, or isolation guard blocked a LynxPay hop
          setIsSuccess(true);
        }
      } else {
        setError('Unexpected response from server. Please try again.');
      }

    } catch (err) {
      console.error('Accept invite error:', err);

      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      // Code already used or expired — show friendly redirect screen
      if (errorCode === 'AUTH_INVALID_CODE') {
        setIsCodeUsed(true);
        return;
      }

      if (errorCode === 'AUTH_INVITE_FAILED' || errorCode === 'AUTH_RESET_PASSWORD_FAILED') {
        const fallback = isSignupContext
          ? 'This verification link is invalid or has expired. Please try signing up again.'
          : 'This invitation link is invalid or has expired. Please contact your administrator.';
        setError(errorMessage || fallback);
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (errorMessage) {
        setError(errorMessage);
      } else {
        setError(isSignupContext
          ? 'Unable to verify your account. The link may have expired. Please try signing up again.'
          : 'Unable to accept invitation. The link may have expired. Please contact your administrator.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success state (for non-redirect flows)
  if (isSuccess) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Account created
          </h1>
          <p className="text-gray-400 mb-8">
            Your account has been set up successfully. You can now sign in.
          </p>
          <Link
            to="/sign-in"
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
          >
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Show "link already used" state — redirect to sign-in
  if (isCodeUsed) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center mb-6">
            <CheckCircle className="w-8 h-8 text-brand" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isSignupContext ? 'Already verified' : 'Account already set up'}
          </h1>
          <p className="text-gray-400 mb-8">
            {isSignupContext
              ? 'Your account has already been verified. You can sign in with your password.'
              : 'This invitation link has already been used. You can sign in with your password.'}
          </p>
          <Link
            to="/sign-in"
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
          >
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Show invalid code state
  if (!hasValidCode) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isSignupContext ? 'Invalid verification link' : 'Invalid invitation link'}
          </h1>
          <p className="text-gray-400 mb-8">
            {isSignupContext
              ? 'This verification link is invalid or has expired. Please try signing up again.'
              : 'This invitation link is invalid or has expired. Please contact your administrator for a new invitation.'}
          </p>
          <Link
            to={isSignupContext ? '/sign-up' : '/sign-in'}
            className="block w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
          >
            {isSignupContext ? 'Go to sign up' : 'Go to sign in'}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      helper={
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-gray-500">Already have an account?</span>
          <Link
            to="/sign-in"
            className="font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Sign in
          </Link>
        </div>
      }
    >
      <div className="space-y-8">
        <div>
          <Link
            to={isSignupContext ? '/sign-up' : '/sign-in'}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:text-brand-hover transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {isSignupContext ? 'Back to sign up' : 'Back to sign in'}
          </Link>
          <div className="text-center mt-6">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
              {isSignupContext ? 'Create password' : 'Set up your account'}
            </h1>
            <p className="text-gray-500 text-sm font-medium">
              {isSignupContext
                ? 'Create a password to complete your registration'
                : 'Create a password to complete your account setup'}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Accept Invite Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Password Field */}
          <div>
            <label htmlFor="password" className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">
              Password
            </label>
            <div className="relative mt-1.5">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                className={`w-full bg-white/[0.03] border ${
                  fieldErrors.password
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                } rounded-2xl px-4 py-3.5 pr-12 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none`}
                placeholder="Create a password"
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-400"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p id="password-error" className="mt-1.5 text-sm text-red-400 ml-1">{fieldErrors.password}</p>
            )}

            {/* Password Strength Indicators */}
            {password && (
              <div className="mt-3 space-y-1.5 ml-1">
                {PASSWORD_REQUIREMENTS.map(req => {
                  const passed = req.test(password);
                  return (
                    <div
                      key={req.id}
                      className={`flex items-center gap-2 text-xs ${
                        passed ? 'text-brand' : 'text-gray-500'
                      }`}
                    >
                      <CheckCircle className={`h-3.5 w-3.5 ${passed ? 'opacity-100' : 'opacity-40'}`} />
                      {req.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">
              Confirm password
            </label>
            <div className="relative mt-1.5">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                aria-invalid={!!fieldErrors.confirmPassword}
                aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
                className={`w-full bg-white/[0.03] border ${
                  fieldErrors.confirmPassword
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                } rounded-2xl px-4 py-3.5 pr-12 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none`}
                placeholder="Confirm your password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-400"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <p id="confirmPassword-error" className="mt-1.5 text-sm text-red-400 ml-1">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 shadow-2xl"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Setting up account...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}

export default AcceptInvite;
